import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Plus, LogOut, Shield, Building2 } from 'lucide-react';
import { SearchInput } from '../common/SearchInput';
import { AddLeadModal } from '../leads/AddLeadModal';
import { useAuth } from '../../context/AuthContext';

export function Topbar({ onMenuClick, globalSearch, setGlobalSearch, onAddLead, showToast }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const getPageMeta = (pathname) => {
    switch (pathname) {
      case '/dashboard':
        return { title: 'Dashboard' };
      case '/leads':
        return { title: 'Leads' };
      case '/channels':
        return { title: 'Channels' };
      case '/reports':
        return { title: 'Reports' };
      case '/chatbot':
        return { title: 'Chatbot Logs' };
      case '/settings':
        return { title: 'Settings' };
      default:
        return { title: 'TBS CRM' };
    }
  };

  const meta = getPageMeta(location.pathname);

  const handleLogout = () => {
    logout();
    if (showToast) showToast('Logged out successfully');
    navigate('/login', { replace: true });
  };

  return (
    <>
      <header
        style={{
          height: '64px',
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E4E4E7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          position: 'sticky',
          top: 0,
          zIndex: 800
        }}
      >
        {/* Left: Mobile Menu Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={onMenuClick}
            className="mobile-menu-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#71717A', display: 'none', padding: '6px' }}
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Center: Client Badge */}
        <div
          className="client-badge-pill"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 12px',
            backgroundColor: '#EBF8FC',
            border: '1px solid #A6E4FB',
            borderRadius: '9999px',
            fontSize: '11.5px',
            fontWeight: 600,
            color: '#1e92c4'
          }}
        >
          <Building2 size={13} color="#2cabe1" />
          <span>Royal Phuket Marina & JLL</span>
        </div>

        {/* Right: Search, Actions, Profile & Logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Search Box */}
          <div className="topbar-search-box" style={{ width: '160px' }}>
            <SearchInput value={globalSearch} onChange={setGlobalSearch} placeholder="Search..." />
          </div>

          {/* + Add Lead Button */}
          <button
            type="button"
            onClick={() => setIsAddModalOpen(true)}
            className="topbar-add-lead-btn"
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#2cabe1',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            <Plus size={16} />
            <span className="add-lead-btn-text">Add Lead</span>
          </button>

          {/* Divider */}
          <div style={{ width: '1px', height: '24px', backgroundColor: '#E4E4E7', flexShrink: 0 }} />

          {/* User Profile Pill & Role */}
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#2cabe1', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '12px', flexShrink: 0 }}>
                  {user.avatar || 'TS'}
                </div>
                <div className="user-meta-info" style={{ display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#09090B', lineHeight: '1.2' }}>{user.name}</span>
                  <span style={{ fontSize: '11px', color: '#83c341', fontWeight: 600, lineHeight: '1.2', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Shield size={10} /> {user.role}
                  </span>
                </div>
              </div>

              {/* Logout Button */}
              <button
                type="button"
                onClick={handleLogout}
                title="Log Out"
                style={{
                  height: '34px',
                  padding: '0 12px',
                  borderRadius: '6px',
                  border: '1px solid #E4E4E7',
                  backgroundColor: '#FFFFFF',
                  color: '#52525B',
                  fontSize: '12.5px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease'
                }}
              >
                <LogOut size={13} color="#71717A" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddLead={onAddLead}
        showToast={showToast}
      />
    </>
  );
}
